import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { controlStyles } from "@/common/components/Select/styles";
import SelectController from "@/common/components/SelectController";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { Option } from "@/common/types/option";
import { FormTransformers } from "@/common/utils/formTransformers";
import { maintenanceSpecialtySchema, MaintenanceSpecialtyValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/maintenanceSpecialty.schema";
import { useCreateEstablishmentCompanyMaintenanceSpecialty } from "@/features/establishments/hooks/maintenanceModels/useCreateEstablishmentCompanyMaintenanceSpecialty";
import { useMaintenanceObjectiveActions } from "@/features/maintenance/objectiveMaintenance/objectiveActions/hooks/useMaintenanceObjectiveActions";
import { MaintenanceObjectiveActionsData } from "@/features/maintenance/objectiveMaintenance/objectiveActions/types/MaintenanceObjectiveActions.type";
import { useMaintenanceObjectiveParts } from "@/features/maintenance/objectiveMaintenance/objectiveParts/hooks/useMaintenanceObjectiveParts";
import { MaintenanceObjectivePartsData } from "@/features/maintenance/objectiveMaintenance/objectiveParts/types/MaintenanceObjectiveParts.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";

interface CreateMaintenanceSpecialtyModalProps {
  establishmentCompany: EstablishmentCompanyData;
  disabled?: boolean;
}

const CreateMaintenanceSpecialtyModal = ({ establishmentCompany, disabled }: CreateMaintenanceSpecialtyModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [searchPart, setSearchPart] = useDebounce('');
  const [searchAction, setSearchAction] = useDebounce('');

  const [systemPartsOptions, setSystemPartsOptions] = useState<Option[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<MaintenanceSpecialtyValidationSchema>({
    resolver: zodResolver(maintenanceSpecialtySchema),
    defaultValues: {
      part: undefined,
      system_part: undefined,
      action: undefined,
    },
  });

  const {
    part,
  } = useWatch({
    control,
  });

  const {
    mutate: createMaintenanceSpecialty,
    isPending: createMaintenanceSpecialtyIsPending,
  } = useCreateEstablishmentCompanyMaintenanceSpecialty({
    establishmentCompanyId: establishmentCompany.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany.id] });

      setIsOpen(false);
    },
    onError: FormTransformers.errorsTransformer<MaintenanceSpecialtyValidationSchema>({
      setError
    }),
  });


  const onSubmit: SubmitHandler<MaintenanceSpecialtyValidationSchema> = (data) => {
    createMaintenanceSpecialty(data);
  }

  const {
    data: partsOptions,
  } = useMaintenanceObjectiveParts({
    filters: {
      search: searchPart,
    },
    mapper: Mapper.mapToOptionsWithItem<MaintenanceObjectivePartsData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  const {
    data: actionsOptions,
  } = useMaintenanceObjectiveActions({
    filters: {
      search: searchAction,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectiveActionsData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  useEffect(() => {
    setTimeout(reset, 500);
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerDisabled={disabled}
        Trigger={(
          <Button
            className="flex flex-col items-center bg-white-100 hover:bg-white-200 disabled:hover:bg-white-100 w-full gap-2 p-2.5 rounded-lg border-2 border-dashed border-white-200 text-blue-800 font-medium"
            operationKlassToCheckPermission="Admin::Establishment::Companies::UpdateMaintenanceModels"
            disabled={disabled}
          >
            <PlusIcon size={16} />

            Adicionar Objeto
          </Button>
        )}
        title="Adicionar Objeto de Manutenção"
        useCancellableFooter
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        confirmButtonIsLoading={createMaintenanceSpecialtyIsPending}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 text-white-800">
            <SelectController
              label="Parte"
              control={control}
              name="part"
              options={partsOptions}
              onInputChange={setSearchPart}
              errorMessage={errors.part?.message || undefined}
              markAsRequired
              triggerOnChange={(value) => {
                setSystemPartsOptions((value.item as MaintenanceObjectivePartsData).system_parts.map((system_part) => ({
                  value: system_part.id,
                  label: system_part.system.display_name,
                })));
              }}
              operationKlassToCheckPermission="Admin::Maintenance::ObjectiveParts::Filter"
            />

            <SelectController
              label="Parte do sistema"
              control={control}
              name="system_part"
              options={systemPartsOptions}
              errorMessage={errors.system_part?.message || undefined}
              markAsRequired
              isDisabled={!part}
              placeholder={!part ? 'Selecione a Parte' : undefined}
              classNames={{
                ...controlStyles,
                placeholder: !part ? () => 'italic' : controlStyles.placeholder,
              }}
              operationKlassToCheckPermission="Admin::Maintenance::ObjectiveParts::Filter"
            />

            <SelectController
              label="Ação"
              control={control}
              name="action"
              options={actionsOptions}
              onInputChange={setSearchAction}
              errorMessage={errors.action?.message || undefined}
              markAsRequired
              operationKlassToCheckPermission="Admin::Maintenance::ObjectiveActions::Filter"
            />
          </form>
        )}
      />
    </>
  );
}

export default CreateMaintenanceSpecialtyModal;
